import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useScanner from '@afosto/utils-react/input/useScanner';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import { useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import FulfilmentLocationFilter from '../FulfilmentLocationFilter';
import FulfilmentLocationRenderer from '../FulfilmentLocationRenderer';
import HandlingListFilter from '../HandlingListFilter';
import HandlingListRenderer from '../HandlingListRenderer';
import PurchaseOrderNumberFilter from '../PurchaseOrderNumberFilter';
import PurchaseOrderNumberRenderer from '../PurchaseOrderNumberRenderer';
import ShipmentCard from '../ShipmentCard';
import ShipmentCardSkeleton from '../ShipmentCard/ShipmentCardSkeleton';
import BulkButton from '@afosto/components-react/BulkButton';
import CardWithStatus from '@afosto/app-components-react/CardWithStatus';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import convertToLetter from '@afosto/utils/numberToLetter';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import filterOptionsQuery from '@afosto/utils-react/datagrid/filterOptionsQuery';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import updateDataGridDefaultView from '@afosto/utils-react/datagrid/updateDataGridDefaultView';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { handling as handlingApi } from '@afosto/fulfilment/api/Handling';
import { index as searchApi } from '@afosto/content/api/Index';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import { fulfilmentLocationFormatter } from '../../utils/formatters';
import ShipmentFilter from '../ShipmentFilter';
import ShipmentRenderer from '../ShipmentRenderer';
import {
  contactFormatter,
  handlingListFormatter,
  methodFormatter,
  shipmentFormatter,
  shipmentsFormatter,
  statusFormatter,
  orderFormatter,
} from './formatters';
import translations from './translations';

const SHIPMENTS_PROFILE_CODE = 'lcs';
const SHIPMENTS_PROFILE_MODEL = 'shipments';
const SHIPMENTS_PROFILE_PATH = 'lcs.shipments';
const SHIPMENTS_PROFILE_NAMESPACE = 'default';

const DataListGridContainer = props => <Grid container spacing={1} {...props} />;

const ShipmentsGrid = ({ showSwitchLocationsDialog }) => {
  const [hasPermissions] = usePermissions();
  const history = useHistory();
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [selectedItems, setSelectedItems] = useState([]);
  const { settings } = useAppState();
  const { locations = [], shipments: shipmentsSettings = {} } = settings;
  const { always_unpack_inbound_shipments: alwaysUnpackInboundShipments = true } =
    shipmentsSettings ?? {};

  const searchIdentifier = {
    code: SHIPMENTS_PROFILE_CODE,
    model: SHIPMENTS_PROFILE_MODEL,
    namespace: SHIPMENTS_PROFILE_NAMESPACE,
  };
  const fixedProperties = [
    {
      key: 'number',
      name: intl.formatMessage({
        id: `dataGrid.columns.${SHIPMENTS_PROFILE_MODEL}.number`,
        defaultMessage: 'Shipment number',
        description: 'The column name for column shipment',
      }),
      type: 'shipment',
    },
  ];
  const defaultProperties = [
    {
      key: 'status',
      name: intl.formatMessage({
        id: `dataGrid.columns.${SHIPMENTS_PROFILE_MODEL}.status`,
        defaultMessage: 'Status',
        description: 'The column name for column status',
      }),
      type: 'status',
      isSortable: false,
    },
    {
      key: 'created_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${SHIPMENTS_PROFILE_MODEL}.createdAt`,
        defaultMessage: 'Created at',
        description: 'The column name for column created at',
      }),
      type: 'datetime',
    },
  ];
  const defaultOutboundProperties = [
    {
      key: 'ship_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${SHIPMENTS_PROFILE_MODEL}.shipAt`,
        defaultMessage: 'Ship at',
        description: 'The column name for column ship at',
      }),
      type: 'datetime',
    },
  ];
  const defaultInboundProperties = [
    {
      key: 'expected_at',
      name: intl.formatMessage({
        id: `dataGrid.columns.${SHIPMENTS_PROFILE_MODEL}.expectedAt`,
        defaultMessage: 'Expected at',
        description: 'The column name for column expected at',
      }),
      type: 'datetime',
    },
  ];
  const ignoreProperties = [
    ...new Set([...defaultProperties, ...defaultOutboundProperties, ...defaultInboundProperties]),
  ].map(property => property.key);
  const { isFetchingProfile, filters, views, properties: filterProperties } = useDataSourceProfile({
    ...searchIdentifier,
    filterTypes: {
      contact_id: 'contact',
      list_id: 'handlingList',
      location: 'fulfilmentLocation',
      method_id: 'shippingMethod',
      order_id: 'order',
      skus: 'sku',
      purchase_order_numbers: 'purchaseOrderNumber',
      waiting_on: 'shipment',
    },
    ignoreFilters: ['indexed_at', 'order_number', 'reference'],
    ignoreProperties: [...ignoreProperties, 'order_number'],
    ignoreSorting: [
      'boolean',
      'contact',
      'fulfilmentLocation',
      'method',
      'shippingMethod',
      'location',
      'array',
      'handlingList',
      'order',
      'sku',
      'purchase_order_numbers',
      'waiting_on',
    ],
    propertyTypes: {
      contact_id: 'contact',
      method_id: 'method',
      list_id: 'handlingList',
      location: 'location',
      order_id: 'order',
      skus: 'array',
      waiting_on: 'shipments',
    },
  });

  const dataSource = useDataGrid(searchQuery(searchIdentifier), {
    gridOptions: {
      columnSelection: true,
      copyGridUrl: true,
      filterSelection: true,
      advancedSort: true,
      viewsSelection: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'shipments',
    },
    modules: ['dataProperties', 'pagination', 'filters', 'sorting', 'views', 'selection', 'search'],
    dataProperties: {
      properties: [
        ...defaultProperties,
        ...defaultInboundProperties,
        ...defaultOutboundProperties,
        ...filterProperties,
        ...fixedProperties,
      ],
      fixedProperties,
    },
    views: {
      storeViewKey: 'shipments_view',
      viewShortcuts: true,
      canCreateViewForTenant:
        sessionManager && sessionManager.hasPermissions(['lcs:shipments:write']),
      defaultView: createDefaultView(intl, {
        properties: [...defaultProperties, ...defaultOutboundProperties],
        filters: [
          {
            key: 'is_listed',
            operator: 'in',
            value: [false],
            type: 'boolean',
          },
          {
            key: 'is_finished',
            operator: 'in',
            value: [false],
            type: 'boolean',
          },
          {
            key: 'is_outbound',
            operator: 'in',
            value: [true],
            type: 'boolean',
          },
          {
            key: 'is_canceled',
            operator: 'in',
            value: [false],
            type: 'boolean',
          },
        ],
        sort: [
          {
            key: 'ship_at',
            direction: 'asc',
          },
        ],
      }),
      views,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(SHIPMENTS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(SHIPMENTS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(SHIPMENTS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(SHIPMENTS_PROFILE_PATH)(repositionedViews),
      onChangeDefaultView: updatedViews =>
        updateDataGridDefaultView(SHIPMENTS_PROFILE_PATH)(updatedViews),
    },
    filters: {
      loadFilterOptions: filterOptionsQuery(searchIdentifier),
      filters,
      fixedFilters: [
        ...(locations?.length
          ? [{ key: 'location', operator: 'in', value: locations.map(locationId => locationId) }]
          : []),
      ],
    },
    selection: {
      allowSelectAll: false,
      onAddSelection: selected => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selected.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selected => {
        const removedIds = selected.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const handlePickShipments = data => async () => {
    try {
      if (selectedItems?.length === 0) {
        return;
      }

      const { selection: selectionModule } = data;
      const { setSelectedItems: setDataSourceSelectedItems } = selectionModule;
      const [firstItem] = selectedItems;

      if (selectedItems.some(item => item?.is_canceled)) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.noCancelledShipments), {
          variant: 'error',
        });
        return;
      }

      if (!selectedItems.every(item => item.is_outbound === firstItem.is_outbound)) {
        enqueueSnackbar(
          intl.formatMessage(translations.feedback.shipmentsNotAllEqualIsOutbound, {
            prop: intl
              .formatMessage(
                translations.properties[firstItem.is_outbound ? 'outbound' : 'inbound'],
              )
              .toLowerCase(),
          }),
          {
            variant: 'error',
          },
        );
        return;
      }

      if (!selectedItems.every(item => item?.location === firstItem?.location)) {
        const locationResponse = await fulfilmentApi.getFulfilmentLocation(firstItem?.location);
        const { data: locationData = {} } = locationResponse;
        enqueueSnackbar(
          intl.formatMessage(translations.feedback.shipmentsNotAllEqualLocation, {
            location: locationData?.name ?? firstItem.location,
          }),
          {
            variant: 'error',
          },
        );
        return;
      }

      const shouldAddLabel = selectedItems.length > 1;

      const sortedShipments = sortArrayByProperty(selectedItems, 'ship_at');
      const [firstShipment] = sortedShipments;
      let dueAt = firstShipment?.ship_at ? new Date(firstShipment.ship_at) : null;

      if (!firstShipment?.is_outbound) {
        dueAt = firstShipment?.expected_at ? new Date(firstShipment.expected_at) : null;
      }

      const promises = selectedItems.map(selectedItem =>
        shipmentsApi.updateShipmentPositions(selectedItem?.id),
      );

      await Promise.allSettled(promises);

      const response = await handlingApi.createHandlingList({
        shipments: selectedItems.map((selectedItem, index) => ({
          id: selectedItem?.id,
          label: shouldAddLabel ? convertToLetter(index + 1) : null,
        })),
        due_at: dueAt,
      });

      setDataSourceSelectedItems([]);
      history.push(`/handling-lists/${response.data.id}/process`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.picklistNotCreated), {
        variant: 'error',
      });
    }
  };

  const handleStartHandlingShipment = async shipment => {
    try {
      if (shipment.list_id) {
        history.push(`/handling-lists/${shipment.list_id}/process`);
        return;
      }

      let dueAt = shipment.ship_at ? new Date(shipment.ship_at) : null;

      if (!shipment?.is_outbound) {
        dueAt = shipment?.expected_at ? new Date(shipment.expected_at) : null;
      }

      // await shipmentsApi.updateShipmentPositions(shipment?.id).catch(() => {
      //   // Do nothing.
      // });

      const createHandlingListResponse = await handlingApi.createHandlingList({
        shipments: [{ id: shipment.id }],
        due_at: dueAt,
      });

      const { data = {} } = createHandlingListResponse;

      if (shipment?.is_outbound) {
        history.push(`/handling-lists/${data.id}/process/pack`);
      } else {
        history.push(`/handling-lists/${data.id}/process`);
      }
    } catch (err) {
      const feedbackKey = shipment?.is_outbound
        ? 'shipmentCanNotBePicked'
        : 'shipmentCanNotBePlaced';

      enqueueSnackbar(intl.formatMessage(translations.feedback[feedbackKey]), {
        variant: 'error',
      });
    }
  };

  const handleClickOutboundShipment = shipment => {
    if (!shipment?.is_handled && hasPermissions(['lcs:handling:write'])) {
      handleStartHandlingShipment(shipment);
      return;
    }

    if (!shipment?.is_boxed && hasPermissions(['lcs:handling:write'])) {
      history.push(`/packing/${shipment.id}`);
      return;
    }

    history.push(`/shipments/${shipment.id}`);
  };

  const handleClickInboundShipment = shipment => {
    if (
      alwaysUnpackInboundShipments &&
      !shipment?.is_boxed &&
      hasPermissions(['lcs:handling:write'])
    ) {
      history.push(`/unpacking/${shipment.id}`);
      return;
    }

    if (!shipment?.is_handled && hasPermissions(['lcs:handling:write'])) {
      handleStartHandlingShipment(shipment);
      return;
    }

    history.push(`/shipments/${shipment.id}`);
  };

  const handleClickShipment = shipment => {
    if (shipment?.is_canceled) {
      history.push(`/shipments/${shipment.id}`);
      return;
    }

    if (shipment?.is_outbound) {
      handleClickOutboundShipment(shipment);
    } else {
      handleClickInboundShipment(shipment);
    }
  };

  const handleScanInput = async inputValue => {
    try {
      if (!inputValue) {
        return;
      }

      const response = await searchApi.searchModel('default', 'lcs', 'shipments', {
        q: [inputValue],
        is_fuzzy: false,
      });
      const { data } = response;
      const hits = (data?.hits ?? []).map(hit => ({
        ...hit.filters,
        ...hit.metadata,
        id: hit.reference,
      }));
      const [firstHit] = hits;

      if (firstHit) {
        handleClickShipment(firstHit);
        return;
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotFound), {
        variant: 'info',
      });
    } catch (err) {
      if (inputValue) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotFound), {
          variant: 'info',
        });
      }
    }
  };

  useScanner(handleScanInput, { disabled: !isMobile || showSwitchLocationsDialog });

  /**
   * Render a shipment in the data list.
   * @param {object} shipment
   * @param {*} SelectionComponent
   * @returns {*}
   */
  const renderShipment = (shipment, { SelectionComponent }) => {
    let cardColor;

    if (shipment?.is_listed) {
      cardColor = 'warning';
    }

    if (shipment?.is_finished) {
      cardColor = 'success';
    }

    return (
      <Grid key={shipment.id} item xs={12}>
        <ShipmentCard
          Container={({ children }) => (
            <CardWithStatus status={cardColor}>{children}</CardWithStatus>
          )}
          SelectionComponent={SelectionComponent}
          shipment={shipment}
          onClick={() => handleClickShipment(shipment)}
          showViewButton
        />
      </Grid>
    );
  };

  /**
   * Render a shipment skeleton for loading state in the data list.
   * @param {string} key
   * @returns {*}
   */
  const renderShipmentSkeleton = key => (
    <Grid key={key} item xs={12}>
      <ShipmentCardSkeleton renderCheckbox />
    </Grid>
  );

  const bulkActions = [
    {
      key: 'pickShipments',
      render: data => (
        <BulkButton onClick={handlePickShipments(data)} key="pickShipments">
          <Typography variant="button">{intl.formatMessage(translations.actions.pick)}</Typography>
        </BulkButton>
      ),
    },
  ];

  const filterFormatters = {
    fulfilmentLocation: FulfilmentLocationFilter,
    handlingList: HandlingListFilter,
    purchaseOrderNumber: PurchaseOrderNumberFilter,
    shipment: ShipmentFilter,
  };

  const filterRenderers = {
    fulfilmentLocation: FulfilmentLocationRenderer,
    handlingList: HandlingListRenderer,
    purchaseOrderNumber: PurchaseOrderNumberRenderer,
    shipment: ShipmentRenderer,
  };

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderShipment}
          renderSkeletonItem={renderShipmentSkeleton}
          component={DataListGridContainer}
          emptyMessage={intl.formatMessage(translations.emptyMessage)}
          bulkActions={bulkActions}
          filterFormatters={filterFormatters}
          filterRenderers={filterRenderers}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{
            shipment: shipmentFormatter,
            shipments: shipmentsFormatter,
            contact: contactFormatter,
            method: methodFormatter,
            handlingList: handlingListFormatter,
            location: fulfilmentLocationFormatter,
            status: statusFormatter,
            order: orderFormatter,
          }}
          filterFormatters={filterFormatters}
          filterRenderers={filterRenderers}
          bulkActions={bulkActions}
        />
      </Hidden>
    </>
  );
};

ShipmentsGrid.propTypes = {
  showSwitchLocationsDialog: PropTypes.bool,
};

ShipmentsGrid.defaultProps = {
  showSwitchLocationsDialog: false,
};

export default ShipmentsGrid;
